import type { FunctionComponent } from "react";
import React, { useEffect, useRef, useState } from "react";
import { getFocusStyle } from "../../theme/accessibility";
import type { ColorToken } from "../../theme/styled";
import { styled, StyleException } from "../../theme/styled";
import { calcRemFromPx } from "../../theme/util";
import { isPromise } from "../../util/isPromise";
import { Divider } from "../dataDisplay/divider/Divider";
import type { IconProps } from "../icons/new-icons";
import { Loader } from "../loader/Loader";
import { Title } from "../typography/Typography";
import { Margin } from "../util/Margin";

/**
 * Dumb component without responsiveness built in -> set size with variant prop.
 * Needs to be wrapped by a MenuList to enable keyboard navigation between items.
 * Used by ActionMenuItem that add responsive design to it etc.
 * This is done so, since both handle different screen sizes a bit differently.
 * The underlying design is anyway the same.
 */

interface MenuItemProps {
  title: string;
  size: "small" | "medium";
  selected?: boolean;
  selectedIndicatorColor?: ColorToken;
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => unknown;
  icon?: FunctionComponent<IconProps> | React.JSX.Element;
  /**
   * padding b4 the icon or text (if no icon is defined)
   */
  horizontalPadding?: string;
  dataTestId?: string;
  dataComponentType?: string;
  role?: string;
  /**
   * If true, the list item will be focused during the first mount. Focus will also be triggered if the value changes from false to true.
   */
  autoFocus?: boolean;

  /**
   * tabindex indicates that its element can be focused, and where it participates in sequential keyboard navigation
   */
  tabIndex?: number;
  /**
   * For disabling item
   */
  disabled?: boolean;
  /**
   * If one wants to have opacity 1 even though disabled
   */
  overrideDisabledOpacity?: boolean;
  /**
   * If true, adds a divider beneath the item
   */
  divider?: boolean;
  /**
   * Sets color of icon and text to red
   */
  isDestructiveItem?: boolean;

  ariaLabel?: string;
}

export function MenuItem({
  dataTestId,
  icon,
  dataComponentType = "menu-item",
  selected,
  selectedIndicatorColor,
  size,
  title,
  disabled,
  divider,
  horizontalPadding,
  overrideDisabledOpacity,
  isDestructiveItem,
  onClick,
  tabIndex,
  role = "menuitem",
  autoFocus,
  ariaLabel,
}: MenuItemProps): React.JSX.Element {
  const itemRef = useRef<HTMLLIElement>(null);
  const [showSpinner, setShowSpinner] = useState(false);

  const itemColor = isDestructiveItem ? "$error-text" : "$text-primary";

  const handleEvent = (e: React.MouseEvent | React.KeyboardEvent): void => {
    if (disabled || showSpinner) {
      return;
    }
    const clickEvent = e.type === "click";
    const keydownEvent = e.type === "keydown" &&
      ((e as React.KeyboardEvent).code === "Enter" ||
        (e as React.KeyboardEvent).code === "Space");

    if (keydownEvent || clickEvent) {
      e.preventDefault();
      e.stopPropagation();

      const result = onClick(e);
      if (isPromise(result)) {
        setShowSpinner(true);
        void result.finally(() => setShowSpinner(false));
      }
    }
  };

  useEffect(() => {
    if (autoFocus) {
      itemRef.current?.focus();
    }
  }, [autoFocus]);

  let Icon = icon;

  if (showSpinner) {
    Icon = (
      <Margin display="flex" right={16}>
        <Loader size={size} />
      </Margin>
    );
  } else if (Icon instanceof Function) {
    Icon = (
      <Icon
        colorToken={itemColor}
        style={{ marginRight: "16px" }}
        size={icon && isSmallSize(size) ? 16 : 24}
      />
    );
  } else if (Icon) {
    Icon = (
      <Margin display="flex" right={16}>
        {Icon}
      </Margin>
    );
  }

  return (
    <>
      <StyledMenuItem
        ref={itemRef}
        onClick={handleEvent}
        onKeyDown={handleEvent}
        disabled={Boolean(disabled) || showSpinner}
        role={role}
        tabIndex={disabled ? -1 : tabIndex || 0}
        aria-disabled={disabled}
        data-test-id={dataTestId}
        data-component-type={dataComponentType}
        isSmall={isSmallSize(size)}
        selected={Boolean(selected)}
        overrideDisabledOpacity={Boolean(overrideDisabledOpacity)}
        horizontalPadding={horizontalPadding}
        aria-label={ariaLabel}
      >
        {selected && (
          <SelectedIndicator
            selectedIndicatorColor={selectedIndicatorColor ||
              "$active-highlight"}
          />
        )}
        {Icon}
        <MenuItemText
          color={itemColor}
          size={size}
          selected={Boolean(selected)}
        >
          {title}
        </MenuItemText>
      </StyledMenuItem>
      {divider && (
        <Divider
          margin={["$spacing-8", "$spacing-0", "$spacing-8", "$spacing-0"]}
        />
      )}
    </>
  );
}

const SelectedIndicator = styled("div")<{
  selectedIndicatorColor: ColorToken;
}>(({ selectedIndicatorColor }) => ({
  width: "3px",
  height: "60%",
  backgroundColor: selectedIndicatorColor,
  position: "absolute",
  left: 0,
}));

const StyledMenuItem = styled("li")<{
  isSmall: boolean;
  selected: boolean;
  disabled: boolean;
  overrideDisabledOpacity: boolean;
  horizontalPadding?: string;
}>(
  ({
    isSmall,
    selected,
    disabled,
    overrideDisabledOpacity,
    horizontalPadding,
  }) => ({
    display: "flex",
    position: "relative",
    boxSizing: "border-box",
    padding: StyleException(["$spacing-8", horizontalPadding || "$spacing-24"]),
    minHeight: isSmall ? "36px" : "42px",
    width: "auto",
    cursor: disabled ? "default" : "pointer",
    pointerEvents: selected ? "none" : "all",
    userSelect: "none",
    fontSize: `${calcRemFromPx(15)}rem`,
    lineHeight: `${calcRemFromPx(20)}rem`,
    overflow: "hidden",
    whiteSpace: "nowrap",
    alignItems: "center",
    verticalAlign: "middle",
    justifyContent: "flex-start",
    textDecoration: "none",
    backgroundColor: "transparent",
    border: 0,
    margin: 0,
    borderRadius: 0,
    appearance: "none",
    WebkitTapHighlightColor: "transparent",
    opacity: disabled ? (overrideDisabledOpacity ? 1 : 0.5) : 1,
    "&:hover": {
      textDecoration: "none",
      backgroundColor: disabled ? "initial" : "$hover-background",
    },
    ...getFocusStyle({
      textDecoration: "none",
      outlineOffset: "-3px",
    }),
  }),
);

const MenuItemText = styled(Title)<{ selected: boolean }>(({ selected }) => ({
  fontWeight: selected ? 600 : "normal",
}));

function isSmallSize(size: "small" | "medium"): boolean {
  return size === "small";
}
